import config from '../config'

const TOKEN_KEY = 'wfp_id_token'
const REFRESH_KEY = 'wfp_refresh_token'
const SESSION_KEY = 'wfp_anon_session'

// --- Token storage ---

export function getToken() {
  return localStorage.getItem(TOKEN_KEY)
}

export function getRefreshToken() {
  return localStorage.getItem(REFRESH_KEY)
}

export function setTokens(idToken, refreshToken) {
  localStorage.setItem(TOKEN_KEY, idToken)
  if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken)
}

export function clearTokens() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(REFRESH_KEY)
}

// --- Anonymous session ---

export function getAnonSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export function setAnonSession(session) {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session))
}

export function clearAnonSession() {
  sessionStorage.removeItem(SESSION_KEY)
  localStorage.removeItem(SESSION_KEY)
}

// --- Token parsing ---

export function parseToken(token) {
  if (!token) return null
  try {
    return JSON.parse(atob(token.split('.')[1]))
  } catch {
    return null
  }
}

export function isLoggedIn() {
  const token = getToken()
  if (!token) return false
  try {
    const payload = parseToken(token)
    return payload.exp * 1000 > Date.now() + 60000
  } catch {
    return false
  }
}

export function getUserFromToken() {
  const token = getToken()
  if (!token) return null
  const payload = parseToken(token)
  if (!payload) return null
  const email = payload.email || ''
  const rawRole = String(payload['custom:role'] || '').trim().toLowerCase()
  const role = ['owner', 'member'].includes(rawRole) ? rawRole : 'member'
  const rawPlan = String(payload['custom:plan'] || '').trim().toLowerCase()
  const plan = ['free', 'starter', 'pro', 'business', 'enterprise'].includes(rawPlan)
    ? rawPlan
    : 'free'
  return {
    id: payload.sub,
    email,
    name: payload.name || email?.split('@')[0] || '',
    plan,
    orgId: payload['custom:orgId'] || null,
    role,
    isPlatformAdmin: false,
  }
}

// --- Token refresh ---

export async function refreshTokenIfNeeded() {
  const token = getToken()
  if (!token) return false

  try {
    const payload = parseToken(token)
    if (!payload) return false

    // Token still has more than 5 minutes - no refresh needed
    if (payload.exp * 1000 - Date.now() > 300000) return true

    const refreshToken = getRefreshToken()
    if (!refreshToken) return false

    const response = await fetch(
      `https://${config.COGNITO_DOMAIN}/oauth2/token`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: config.CLIENT_ID,
          refresh_token: refreshToken,
        }),
      }
    )

    const data = await response.json()
    if (data.id_token) {
      setTokens(data.id_token, null)
      return true
    }
    return false
  } catch {
    return false
  }
}

// --- Auth flows ---

export function redirectToLogin() {
  const url =
    `https://${config.COGNITO_DOMAIN}/login` +
    `?client_id=${config.CLIENT_ID}` +
    `&response_type=code` +
    `&scope=email+openid+profile` +
    `&redirect_uri=${encodeURIComponent(config.REDIRECT_URI)}`
  window.location.href = url
}

export function redirectToSignUp() {
  const url =
    `https://${config.COGNITO_DOMAIN}/signup` +
    `?client_id=${config.CLIENT_ID}` +
    `&response_type=code` +
    `&scope=email+openid+profile` +
    `&redirect_uri=${encodeURIComponent(config.REDIRECT_URI)}`
  window.location.href = url
}

export function redirectToForgotPassword() {
  const url =
    `https://${config.COGNITO_DOMAIN}/forgotPassword` +
    `?client_id=${config.CLIENT_ID}` +
    `&response_type=code` +
    `&scope=email+openid+profile` +
    `&redirect_uri=${encodeURIComponent(config.REDIRECT_URI)}`
  window.open(url, '_blank', 'noopener,noreferrer')
}

export function logout() {
  clearTokens()
  const url =
    `https://${config.COGNITO_DOMAIN}/logout` +
    `?client_id=${config.CLIENT_ID}` +
    `&logout_uri=${encodeURIComponent(config.LOGOUT_URI)}`
  window.location.href = url
}

export async function exchangeCodeForToken(code) {
  try {
    const response = await fetch(
      `https://${config.COGNITO_DOMAIN}/oauth2/token`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: config.CLIENT_ID,
          code,
          redirect_uri: config.REDIRECT_URI,
        }),
      }
    )
    const data = await response.json()
    if (data.id_token) {
      setTokens(data.id_token, data.refresh_token)
      return true
    }
    return false
  } catch {
    return false
  }
}
